// «Совет ИИ» внизу идущей тренировки: по сделанному и оставшемуся ИИ говорит, хватит ли на
// сегодня, можно ли добавить или где поправить вес. Тренировка уходит с устройства (на сервере
// она может быть неполной), ответ не хранится на сервере — последний помнит вкладка.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button, Card, ErrorText } from '@/components/ui'
import { formatTime } from '@/lib/format'
import type { ActiveWorkout } from '@/offline/activeSession'
import { aiApi, aiKeys, useAiStatus } from '@/pages/ai/api'
import { DISCLAIMER, Markdown } from '@/pages/ai/parts'

import { checkRequest, doneCount } from './logic'

const STORE_KEY = 'health.workout.aiCheck'

type Answer = { workoutId: string; reply: string; at: string; done: number }

function loadAnswer(workoutId: string): Answer | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORE_KEY) ?? 'null') as Answer | null
    return value?.workoutId === workoutId ? value : null
  } catch {
    return null
  }
}

function storeAnswer(answer: Answer): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(answer))
  } catch {
    // приватный режим — ответ просто не переживёт перезагрузку
  }
}

export function AiCheckCard({ workout }: { workout: ActiveWorkout }) {
  const client = useQueryClient()
  const status = useAiStatus()
  const [answer, setAnswer] = useState<Answer | null>(() => loadAnswer(workout.id))
  const ask = useMutation({
    mutationFn: () => aiApi.sessionCheck(checkRequest(workout)),
    onSuccess: (data) => {
      const next = { workoutId: workout.id, reply: data.reply, at: new Date().toISOString(), done: doneCount(workout) }
      storeAnswer(next)
      setAnswer(next)
    },
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.usage }),
  })
  // ИИ не настроен — посреди тренировки карточка без пользы не нужна.
  if (status.data?.configured !== true) return null

  return (
    <Card
      title="Совет ИИ"
      subtitle={answer ? null : 'Хватит на сегодня или можно добавить — по тому, что уже сделано'}
    >
      <div className="flex flex-col gap-3">
        {answer ? (
          <div className="flex flex-col gap-1.5">
            <Markdown text={answer.reply} />
            <p className="text-xs text-muted">
              В {formatTime(answer.at)}, после {answer.done} подх. · {DISCLAIMER}
            </p>
          </div>
        ) : null}
        <Button icon="ai" block loading={ask.isPending} onClick={() => ask.mutate()}>
          {answer ? 'Спросить ещё раз' : 'Спросить ИИ'}
        </Button>
        <ErrorText error={ask.error} />
      </div>
    </Card>
  )
}
